import { useAuth } from "@clerk/clerk-react";
import { Grid, Slide } from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import {
  ENTER_DELAY,
  EXIT_DELAY,
  SlideAnimationContext,
  SlideAnimationProvider,
} from "contexts/SlideAnimation";
import { SplashPage, SubscriptionContext } from "contexts/Subscription";
import { useContext, useEffect, useState } from "react";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import {
  Organization,
  OrganizationProfile,
  SearchParams,
} from "schemas/dashboard";
import { Map } from "schemas/functions";
import { Plans } from "schemas/payments";
import { routes } from "schemas/routes";
import theme from "styles/theme";

import Loading from "components/Loading";
import { PAGE_VISITED } from "constants/trackingProps";
import { fetcherAuth } from "utils/api";
import { isPage } from "utils/general";
import {
  DISPLAYED_INSTAGRAM_ONBOARDING_PAYWALL,
  INSTAGRAM_ONBOARDING_CHOICE,
} from "utils/localStorage";
import { getSource, identifyUser, trackEvent } from "utils/tracking";
import { useSubscription } from "utils/useSubscription";

import AboutForm from "../Onboarding/AboutForm";
import CategoryForm from "../Onboarding/CategoryForm";
import { AuthAction } from "../schema";
import InstagramOnboardingPreference from "./Preference";
import InstagramOnboardingSignup from "./Signup";
import { instagramOnboardingNavigate } from "./helpers";
import { InstagramOnboardingOptions } from "./schema";
import styles from "./styles";

const InstagramOnboardingFlow = () => {
  // Contexts
  const {
    profile,
    currentOrg,
    currentUser,
    setCurrentUser,
    setCurrentOrg,
    setProfile,
    setShowGamificationProfile,
  } = useContext(OrganizationUserContext);
  const { setErrorAlert, setAlert } = useContext(AlertContext);
  const { setSubscription } = useContext(SubscriptionContext);
  const { slideOutUp, slideInUp, slideOutDown, slideInDown, slideProps } =
    useContext(SlideAnimationContext);
  const { handleCheckout } = useSubscription();

  const navigate = useNavigate();
  const location = useLocation();
  const source = getSource();
  const { getToken } = useAuth();
  const [searchParams] = useSearchParams();

  // States
  const [onboardingForm, setOnboardingForm] = useState<Map>({
    name: currentUser?.name || currentOrg?.name || "",
    location: "",
    profileTypes: [],
    // These fields are auto-filled when user sign up
    email: "",
    organizations: [],
    organizationId: "",
    organizationName: "",
    type: "influencer",
  });
  const [fetchUserLoading, setFetchUserLoading] = useState(true);
  const [hasRedirected, setHasRedirected] = useState(false);

  // Constants
  const referralCode = searchParams.get(SearchParams.REFERRAL_CODE);
  const option =
    (localStorage.getItem(
      INSTAGRAM_ONBOARDING_CHOICE,
    ) as InstagramOnboardingOptions) || searchParams.get(SearchParams.OPTION);
  const emailMatch = location.search?.match(/email=([^&]*)/);
  const email = emailMatch ? decodeURIComponent(emailMatch[1]) : "";

  const fetchUserInfo = async () => {
    if (
      currentOrg?.id &&
      currentOrg?.id > 0 &&
      profile?.id &&
      profile?.id > 0
    ) {
      redirectToRightPage(currentOrg, profile);
      setFetchUserLoading(false);
      return;
    }
    setFetchUserLoading(true);
    try {
      const res = await fetcherAuth(getToken, `/api/organization/auth/user`);
      if (res) {
        identifyUser(res.organizationUser.id);
        setProfile(res.organizationProfile);
        setCurrentUser(res.organizationUser);
        setCurrentOrg(res.organization);
        setSubscription(res.subscription);
        localStorage.setItem(
          `${DISPLAYED_INSTAGRAM_ONBOARDING_PAYWALL}-${res.organization.id}`,
          "true",
        );
        redirectToRightPage(res.organization, res.organizationProfile);
        setHasRedirected(true);
      } else {
        slideInUp();
      }
      setFetchUserLoading(false);
    } catch (error) {
      handleClerkUserSignUp();
    }
  };

  const handleClerkUserSignUp = async () => {
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/auth/signup`,
        "POST",
        {},
        {
          referralCode,
          source: referralCode ? "referral" : source,
          isInstagramOnboardingFlow: true,
          option,
        },
      );
      setFetchUserLoading(false);
      identifyUser(res.organizationUser.id);
      setCurrentUser(res.organizationUser);
      setCurrentOrg(res.organization);
      setShowGamificationProfile(res.showGamificationProfile);
      if (
        // @ts-ignore
        typeof window.fbq !== "undefined" &&
        // @ts-ignore
        typeof window.fbq === "function"
      ) {
        // @ts-ignore
        window.fbq("track", "CompleteRegistration");
      }
      if (
        // @ts-ignore
        typeof window.ttq !== "undefined" &&
        // @ts-ignore
        typeof window.ttq.track === "function"
      ) {
        // @ts-ignore
        window.ttq.track("CompleteRegistration");
      }
      if (
        // @ts-ignore
        typeof window.gtag !== "undefined" &&
        // @ts-ignore
        typeof window.gtag === "function"
      ) {
        // @ts-ignore
        window.gtag("event", "conversion", {
          send_to: "AW-10904251795/n3zWCNqZopAcEJPbxs8o",
        });
      }
    } catch (error) {
      setErrorAlert(error);
      setFetchUserLoading(false);
    }
  };

  const handleBack = async () => {
    await slideOutDown();
    trackEvent("Instagram Onboarding Form - Back Button Pressed", {
      route: location.pathname,
    });
    navigate(
      `/${instagramOnboardingNavigate(location.pathname, "back")}${
        location.search
      }`,
    );
    slideInDown();
  };

  const handleSkip = () => {
    trackEvent("Instagram Onboarding Form - Skip Button Pressed", {
      route: location.pathname,
    });
    moveNext();
  };

  const handleNext = async () => {
    trackEvent("Instagram Onboarding Form - Next Button Pressed", {
      route: location.pathname,
    });
    saveOnboardingForm();
  };

  const moveNext = async () => {
    await slideOutUp();
    navigate(
      `/${instagramOnboardingNavigate(location.pathname, "next")}${
        location.search
      }`,
    );
    slideInUp();
  };

  const saveOnboardingForm = async () => {
    const onboardingFormReq = {
      ...onboardingForm,
      page: location.pathname,
    };
    await moveNext();
    try {
      const { organizationProfile } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/onboarding`,
        "PUT",
        {},
        onboardingFormReq,
      );
      setProfile({ ...organizationProfile });
      setOnboardingForm((prev) => ({ ...prev, ...organizationProfile }));
    } catch (error) {
      setAlert(
        error?.message ||
          "Unable to save your information. Please reload and try again",
        "error",
      );
    }
  };

  const redirectToRightPage = (
    organization: Organization,
    profile: OrganizationProfile,
  ) => {
    slideInUp();
    setOnboardingForm((prev) => ({
      ...prev,
      ...profile,
      name: organization?.name,
    }));
    localStorage.setItem(
      `${DISPLAYED_INSTAGRAM_ONBOARDING_PAYWALL}-${organization.id}`,
      "true",
    );
    setHasRedirected(true);
    if (!isPage(routes.preferencesInstagramOnboarding)) {
      return;
    }
    if (
      option === InstagramOnboardingOptions.done_for_you &&
      !currentOrg?.isVaUser
    ) {
      handleCheckoutVa();
      return;
    }
    navigate(`/${routes.aboutInstagramOnboarding}${location.search}`);
  };

  const needsRedirection = Boolean(
    currentOrg?.id &&
      profile?.id &&
      profile?.id > 0 &&
      !hasRedirected &&
      option,
  );

  const handleCheckoutVa = () => {
    handleCheckout(
      Plans.VA_YEARLY,
      window.location.pathname,
      source,
      SplashPage.instagram,
    );
  };

  useEffect(() => {
    trackEvent(PAGE_VISITED);
    if (needsRedirection && currentOrg && profile) {
      redirectToRightPage(currentOrg, profile);
    } else {
      fetchUserInfo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id && profile?.id && option]);

  return fetchUserLoading ? (
    <Loading />
  ) : (
    <Grid container sx={styles.root}>
      <Slide
        in={slideProps.in}
        direction={slideProps.direction}
        easing={{
          enter: theme.transitions.easing.easeInOut,
          exit: theme.transitions.easing.easeIn,
        }}
        timeout={{
          enter: ENTER_DELAY,
          exit: EXIT_DELAY,
        }}
      >
        <Grid sx={styles.formContainer} item xs={12}>
          <Routes>
            <Route
              index
              element={
                <Navigate
                  to={`/${routes.preferencesInstagramOnboarding}${location.search}`}
                />
              }
            />
            <Route
              path="/preferences/*"
              element={
                <InstagramOnboardingPreference
                  handleCheckoutVa={handleCheckoutVa}
                />
              }
            />
            <Route
              path="/sign-up/*"
              element={
                <InstagramOnboardingSignup
                  email={email}
                  action={AuthAction.SIGN_UP}
                  option={option as InstagramOnboardingOptions}
                />
              }
            />
            <Route
              path="/sign-in/*"
              element={
                <InstagramOnboardingSignup
                  email={email}
                  action={AuthAction.SIGN_IN}
                  option={option as InstagramOnboardingOptions}
                />
              }
            />
            <Route
              path="/about/*"
              element={
                <AboutForm
                  onboardingForm={onboardingForm}
                  setOnboardingForm={setOnboardingForm}
                  handleNext={handleNext}
                  handleSkip={handleSkip}
                  hideDiscoveryQuestion={true}
                />
              }
            />
            <Route
              path="/category/*"
              element={
                <CategoryForm
                  onboardingForm={onboardingForm}
                  handleBack={handleBack}
                  handleSkip={handleSkip}
                  moveNext={() => {
                    trackEvent("Onboarding Form - Next Button Pressed", {
                      route: location.pathname,
                    });

                    moveNext();
                  }}
                />
              }
            />
          </Routes>
        </Grid>
      </Slide>
    </Grid>
  );
};

export default function Onboarding() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    trackEvent("Instagram Onboarding Flow - Redirected to Onboarding Flow");
    navigate(`/${routes.signUp}${location.search}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <SlideAnimationProvider>
      <InstagramOnboardingFlow />
    </SlideAnimationProvider>
  );
}
